import React, { useState, useEffect } from "react";
import { Search, FileText, Image, Video, LayoutGrid, X } from "lucide-react";
import apiEndpoints from "../../apiconfig";

const MessagePopup = ({
  onClose,
  onSelectTemplate,
  showOnlyMarketing = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedLanguage, setSelectedLanguage] = useState("all");

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await fetch(apiEndpoints.managetemplate, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });

        const data = await response.json();

        if (data.status === "success" && Array.isArray(data.data)) {
          const formatted = data.data.map((t) => {
            const body = t.components?.find((c) => c.type === "BODY");
            const header = t.components?.find((c) => c.type === "HEADER");
            let type = "text";
            if (header?.format === "IMAGE") type = "image";
            else if (header?.format === "VIDEO") type = "video";
            else if (header?.format === "DOCUMENT") type = "file";

            return {
              id: t.id,
              name: t.name,
              categoryName: t.category || "General",
              languageName: t.language || "Unknown",
              meta_status: t.status || "Pending",
              template_json: JSON.stringify({ components: t.components }),
              templateBody: body?.text || "No content",
              templateType: type,
              local_media: t.local_media || null,
            };
          });
          setTemplates(formatted);
        } else {
          throw new Error("No templates found");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const categories = ["all", ...new Set(templates.map((t) => t.categoryName))];
  const languages = ["all", ...new Set(templates.map((t) => t.languageName))];

  const filteredTemplates = templates.filter((t) => {
    const searchMatch = t.name.toLowerCase().includes(searchTerm.toLowerCase());
    const languageMatch =
      selectedLanguage === "all" || t.languageName === selectedLanguage;

    // 🔥 For GROUP: Only show Marketing templates
    if (showOnlyMarketing) {
      const isMarketing =
        t.categoryName?.toLowerCase() === "marketing" ||
        t.categoryName?.toLowerCase() === "promotional";
      return isMarketing && searchMatch && languageMatch;
    }

    // 🔥 For ALL OTHER PAGES: Show everything
    return searchMatch && languageMatch;
  });

  const renderIcon = (type) => {
    const iconProps = {
      size: 32,
      className: "text-[#25D366]",
    };
    switch (type) {
      case "image":
        return <Image {...iconProps} />;
      case "video":
        return <Video {...iconProps} />;
      case "file":
        return <FileText {...iconProps} />;
      case "carousel":
        return <LayoutGrid {...iconProps} />;
      default:
        return <FileText {...iconProps} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "bg-green-100 text-green-800 border border-green-200";
      case "REJECTED":
        return "bg-red-100 text-red-700 border border-red-200";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      case "IN_REVIEW":
        return "bg-blue-100 text-blue-800 border border-blue-200";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-200";
    }
  };

  const handleTemplateSelect = (template) => onSelectTemplate(template);
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setSelectedLanguage("all");
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      style={{ fontFamily: "Montserrat", alignItems: "flex-start" }}
    >
      <div
        className="bg-white w-full max-w-5xl rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
        style={{
          marginTop: "2rem",
        }}
      >
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 p-4 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-800">
            Select Message Template
          </h2>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative">
              <Search
                className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search templates..."
                className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#25D366] outline-none"
              />
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-md transition"
            >
              <X className="text-gray-500" size={18} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-gray-700 text-sm font-medium">Category:</span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-[#25D366] outline-none"
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c === "all" ? "All" : c}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-gray-700 text-sm font-medium">Language:</span>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-[#25D366] outline-none"
            >
              {languages.map((l) => (
                <option key={l} value={l}>
                  {l === "all" ? "All" : l}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={clearFilters}
            className="ml-auto text-sm text-gray-600 underline hover:text-[#25D366]"
          >
            Clear Filters
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <div className="h-8 w-8 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-10">
              <p className="text-red-500 mb-2">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-[#25D366] text-white rounded-md text-sm hover:bg-[#1CB85C]"
              >
                Retry
              </button>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              No templates found matching your filters.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {filteredTemplates.map((t) => (
                <div
                  key={t.id}
                  onClick={() => handleTemplateSelect(t)}
                  className="border border-gray-200 rounded-xl bg-white p-4 shadow-sm hover:shadow-lg transition cursor-pointer"
                >
                  <div className="bg-gray-50 flex flex-col items-center justify-center rounded-lg p-3 h-28">
                    {renderIcon(t.templateType)}
                    <div className="mt-2 flex gap-1 flex-wrap justify-center">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          t.meta_status
                        )}`}
                      >
                        {t.meta_status}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                        {t.templateType}
                      </span>
                    </div>
                  </div>

                  <p className="text-gray-800 mt-2 font-semibold text-sm truncate">
                    {t.name}
                  </p>
                  <p className="text-gray-600 text-xs mt-1 line-clamp-2">
                    {t.templateBody}
                  </p>
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>{t.categoryName}</span>
                    <span>{t.languageName}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center border-t border-gray-200 bg-gray-50 p-3 sticky bottom-0">
          <p className="text-sm text-gray-600">
            Showing {filteredTemplates.length} of {templates.length} templates
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[#25D366] text-white rounded-md text-sm hover:bg-[#1CB85C]"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagePopup;
